/**
 * Intersection Observer action for Svelte elements.
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * CLASSES ADDED
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * STATE CLASSES (transient - added when condition is true, removed when false):
 *   • `inview` - Element is currently visible in viewport
 *   • `at-{X}` - Element is currently at X% visibility
 *                Examples: at-0, at-20, at-50, at-80
 * 
 * MARKER CLASSES (permanent - never removed once added):
 *   • `seen-{X}` - Element has crossed X% threshold AND left it at least once
 *                  Examples: seen-0, seen-20, seen-50, seen-80
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * CSS ANIMATION PATTERNS
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * ONE-TIME ANIMATIONS (play only on first crossing):
 * ────────────────────────────────────────────────────────────────────────────
 * 
 *   .my-card.at-50:not(.seen-50) {
 *     animation: slide-up 0.8s forwards;
 *   }
 * 
 *   Also set initial state before animation 
 *   .my-block:not(.seen-20) {
 *     opacity: 0;
 *     transform: translateY(20px);
 *   }
 * 
 * REPEATING ANIMATIONS (play every time threshold is crossed):
 * ────────────────────────────────────────────────────────────────────────────
 * 
 *   .my-block.at-20 {
 *     animation: pulse 0.5s;
 *   }
 * 
 * STATE-BASED STYLING (not animations, just conditional styles):
 * ────────────────────────────────────────────────────────────────────────────
 * 
 *   .my-block.at-20 {
 *     opacity: 1;
 *     transform: scale(1);
 *   }
 * 
 *   .my-block:not(.at-20) {
 *     opacity: 0.5;
 *     transform: scale(0.95);
 *   }
 * 
 * FINAL STATE (after animation completes):
 * ────────────────────────────────────────────────────────────────────────────
 * 
 *   .my-block.seen-20 {
 *     opacity: 0;
 *     pointer-events: none;
 *     height: 0;
 *   }
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * HOW IT WORKS (step-by-step example with 20% threshold)
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * 1️⃣  Element enters 20% threshold (first time):
 *     ✅ at-20 is added
 *     ❌ seen-20 is NOT added yet
 *     🎬 Selector .at-20:not(.seen-20) matches
 *        → Animation starts and runs to completion
 * 
 * 2️⃣  Element stays at 20%+ visibility:
 *     ✅ at-20 stays on
 *     ❌ seen-20 still not added
 *     ✨ Animation continues uninterrupted
 * 
 * 3️⃣  Element drops below 20% (leaving threshold):
 *     ❌ at-20 is removed
 *     ✅ seen-20 is NOW added (permanent marker)
 * 
 * 4️⃣  Element enters 20% again (second time, if once=false):
 *     ✅ at-20 is added
 *     ✅ seen-20 is already there (permanent)
 *     ❌ Selector .at-20:not(.seen-20) doesn't match
 *        → One-time animations don't play
 *     ✅ But .at-20 alone still matches
 *        → Repeating animations DO play
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * USAGE EXAMPLE
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * <div class="my-element" use:inview={{ threshold: [0, 0.2, 0.5, 0.8] }}>
 *   Content fades in at 20% visibility
 * </div>
 * 
 * <style>
 *   .my-element:not(.seen-20) {
 *     opacity: 0;
 *     transform: translateY(30px);
 *   }
 *   
 *   .my-element.at-20:not(.seen-20) {
 *     animation: fade-in-up 0.8s forwards;
 *   }
 *   
 *   .my-element.seen-20 {
 *     opacity: 1;
 *     transform: translateY(0);
 *   }
 * </style>
 * 
 */

// Global RAF batching for all inview updates
const pending_updates = new Set();
let global_raf = null;

function schedule_dom_update(callback) {
  pending_updates.add(callback);
  if (!global_raf) {
    global_raf = requestAnimationFrame(() => {
      pending_updates.forEach(cb => cb());
      pending_updates.clear();
      global_raf = null;
    });
  }
}

// Shared observer cache - one observer per unique configuration
const observer_cache = new Map();

function get_observer_key(threshold, root, root_margin) {
  return JSON.stringify({ threshold, root, root_margin });
}

function get_shared_observer(threshold, root, root_margin) {
  const key = get_observer_key(threshold, root, root_margin);
  
  if (!observer_cache.has(key)) {
    const node_handlers = new Map();
    
    const shared_callback = (entries) => {
      entries.forEach((entry) => {
        const handler = node_handlers.get(entry.target);
        if (handler) {
          handler(entry);
        }
      });
    };
    
    const observer = new IntersectionObserver(shared_callback, {
      root,
      rootMargin: root_margin,
      threshold
    });
    
    observer_cache.set(key, {
      observer,
      node_handlers,
    });
  }
  
  return observer_cache.get(key);
}

export default function inview(node, { 
  active = true,
  /** @type {boolean} [once=true] - Stop observing after the first intersection. */
  once = true,
  /** 
   * @type {Array<Number>} [threshold=[0,0.2,0.5,0.8]] - Thresholds for the intersection ratios.
   * ⚠️ WARNING: Each threshold step triggers intersection callbacks. Using many thresholds
   * (e.g. [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]) can impact scroll performance.
   * Recommend keeping it to 1-4 thresholds max for optimal performance.
   */
  threshold = [0, 0.2, 0.5, 0.8],
  /** @type {Function} [oninview] - Callback function for intersection events. */
  oninview,
  /** @type {HTMLElement} [root] - The root element for the intersection observer. */
  root,
  /** @type {string} [root_margin] - Margin around the root. */
  root_margin,
} = {}) {
  let observer_data;
  let has_been_in_view = false;
  let seen_thresholds = new Set(); // Permanent markers (only added AFTER leaving threshold)
  let current_at_thresholds = new Set(); // Current at-X classes
  let thresholds = Array.isArray(threshold) ? threshold : [threshold];
  let pending_dom_update = false; // Throttle updates per node
  let last_ratio = -1; // Track to avoid redundant updates
  let last_entry = null; // Store latest entry
  let pending_callback = null; // Reference to scheduled RAF callback for cleanup
  
  // Initialize from existing DOM classes (in case of hydration or edit mode toggle)
  thresholds.forEach((t) => {
    const percentage = Math.round(t * 100);
    if (node.classList.contains(`seen-${percentage}`)) {
      seen_thresholds.add(percentage);
    }
    if (node.classList.contains(`at-${percentage}`)) {
      current_at_thresholds.add(percentage);
    }
  });

  /**
   * Apply DOM updates for current intersection state
   */
  const apply_dom_updates = (entry) => {
    const min_threshold = Math.min(...thresholds);
    
    // CRITICAL: Sync seen_thresholds from DOM before processing
    // This prevents desync during fast scrolling when classes are added
    // to DOM but JS state hasn't caught up
    thresholds.forEach((t) => {
      const percentage = Math.round(t * 100);
      if (node.classList.contains(`seen-${percentage}`) && !seen_thresholds.has(percentage)) {
        seen_thresholds.add(percentage);
      }
    });

    if (entry.isIntersecting && entry.intersectionRatio >= min_threshold) {
      // Element is entering view
      node.classList.add('inview');
      node.classList.remove('not-inview');
      has_been_in_view = true;

      // Callback
      if (oninview) oninview(entry);

      // Process each threshold
      thresholds.forEach((t) => {
        const percentage = Math.round(t * 100);
        const is_at_threshold = entry.intersectionRatio >= t - 0.001; // Small epsilon
        const was_at_threshold = current_at_thresholds.has(percentage);

        if (is_at_threshold && !was_at_threshold) {
          // Entering this threshold - add at-X class
          node.classList.add(`at-${percentage}`);
          current_at_thresholds.add(percentage);
          
          // Note: seen-X is NOT added here! It's only added when LEAVING the threshold
          // This allows .at-X:not(.seen-X) to match for the entire duration
        } else if (!is_at_threshold && was_at_threshold) {
          // Leaving this threshold - remove at-X and add seen-X to DOM
          node.classList.remove(`at-${percentage}`);
          current_at_thresholds.delete(percentage);
          
          // Add seen-X to DOM (Set was already updated synchronously)
          if (!node.classList.contains(`seen-${percentage}`)) {
            node.classList.add(`seen-${percentage}`);
          }
        }
      });
    } else if (has_been_in_view && !entry.isIntersecting) {
      // Element is leaving view completely
      node.classList.remove('inview');
      
      // Remove at-X and add seen-X to DOM (Set was already updated synchronously)
      current_at_thresholds.forEach((percentage) => {
        node.classList.remove(`at-${percentage}`);
        
        if (!node.classList.contains(`seen-${percentage}`)) {
          node.classList.add(`seen-${percentage}`);
        }
      });
      current_at_thresholds.clear();

      if (once) {
        // With once=true: unobserve, don't add not-inview
        unobserve_node();
      } else {
        // With once=false: add not-inview, keep observing
        node.classList.add('not-inview');
      }
    }
    
    pending_dom_update = false;
  };

  /**
   * Handle intersection changes for this specific node
   */
  const handle_intersection = (entry) => {
    // Skip redundant updates - only process if ratio actually changed significantly
    const ratio_changed = Math.abs(entry.intersectionRatio - last_ratio) > 0.01;
    if (!ratio_changed && last_ratio >= 0) return;
    
    last_ratio = entry.intersectionRatio;
    last_entry = entry;
    
    // CRITICAL: Update seen_thresholds Set IMMEDIATELY (synchronously)
    // This ensures update() can read the correct state even if called before RAF runs
    const min_threshold = Math.min(...thresholds);
    if (entry.isIntersecting && entry.intersectionRatio >= min_threshold) {
      has_been_in_view = true;
      
      // Check which thresholds are being left (to mark as seen)
      thresholds.forEach((t) => {
        const percentage = Math.round(t * 100);
        const is_at_threshold = entry.intersectionRatio >= t - 0.001;
        const was_at_threshold = current_at_thresholds.has(percentage);
        
        if (!is_at_threshold && was_at_threshold) {
          // Leaving threshold - mark as seen immediately
          if (!seen_thresholds.has(percentage)) {
            seen_thresholds.add(percentage);
          }
        }
      });
    } else if (has_been_in_view && !entry.isIntersecting) {
      // Leaving view - mark all current thresholds as seen immediately
      current_at_thresholds.forEach((percentage) => {
        if (!seen_thresholds.has(percentage)) {
          seen_thresholds.add(percentage);
        }
      });
    }
    
    // Throttle: only schedule one DOM update per node per frame
    if (pending_dom_update) return;
    
    pending_dom_update = true;
    
    // Store callback reference for cleanup in update()/destroy()
    pending_callback = () => apply_dom_updates(last_entry);
    schedule_dom_update(pending_callback);
  };

  /**
   * Remove node from shared observer
   */
  const unobserve_node = () => {
    if (observer_data) {
      observer_data.observer.unobserve(node);
      observer_data.node_handlers.delete(node);
      
      // Clean up empty observer
      if (observer_data.node_handlers.size === 0) {
        const key = get_observer_key(thresholds, root, root_margin);
        observer_cache.delete(key);
      }
      
      observer_data = null;
    }
  };

  /**
   * Add node to shared observer
   */
  const observe_node = () => {
    observer_data = get_shared_observer(thresholds, root, root_margin);
    observer_data.node_handlers.set(node, handle_intersection);
    observer_data.observer.observe(node);
  };

  // Initialize if active
  if (active) {
    observe_node();
  }

  return {
    update(new_params) {
      // Flush pending DOM updates before reading state
      // If we have a pending RAF callback, execute it immediately to ensure
      // DOM is up-to-date before we read from it
      if (pending_callback) {
        pending_updates.delete(pending_callback);
        pending_callback(); // Execute immediately
        pending_callback = null;
        pending_dom_update = false;
      }
      
      // Update params first
      ({ active, once, threshold, oninview, root, root_margin } = new_params);
      thresholds = Array.isArray(threshold) ? threshold : [threshold];
      
      // Preserve state from DOM before cleaning up
      seen_thresholds.clear();
      thresholds.forEach((t) => {
        const percentage = Math.round(t * 100);
        if (node.classList.contains(`seen-${percentage}`)) {
          seen_thresholds.add(percentage);
        }
      });

      current_at_thresholds.clear();
      thresholds.forEach((t) => {
        const percentage = Math.round(t * 100);
        if (node.classList.contains(`at-${percentage}`)) {
          current_at_thresholds.add(percentage);
        }
      });

      // Preserve has_been_in_view if element is currently visible or has seen anything
      if (node.classList.contains('inview') || seen_thresholds.size > 0) {
        has_been_in_view = true;
      }

      // Clean up old observer
      unobserve_node();

      // Reinitialize if active
      if (active) {
        observe_node();
      }
    },

    destroy() {
      // Cancel pending DOM updates to prevent errors on unmounted nodes
      if (pending_callback) {
        pending_updates.delete(pending_callback);
        pending_callback = null;
      }
      unobserve_node();
    },
  };
}
